import type {
  StudioCapabilityKey,
  StudioCapabilityObservation,
  StudioPresentationProfile,
  StudioProbeFailure,
} from '@/services/studio-presentation-profile'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

type RuntimeCopy = StudioDictionary['settings']['runtime']

/** Capability key -> the dictionary field that names it. */
const CAPABILITY_COPY_KEYS: Record<
  StudioCapabilityKey,
  keyof RuntimeCopy['capabilities']
> = {
  'platform-connection': 'platformConnection',
  'local-auth': 'localAuth',
  'google-auth': 'googleAuth',
  'local-publish': 'localPublish',
  'cloud-publish': 'cloudPublish',
  'custom-domain': 'customDomain',
  billing: 'billing',
  analytics: 'analytics',
  'external-integrations': 'externalIntegrations',
}

function displayIdentity(value: string | null, unknown = '—'): string {
  return value ?? unknown
}

/**
 * The sentence for a probe failure, in the reader's language.
 *
 * `copy.probeFailures` is a `Record<StudioProbeFailure['code'], string>`, so a
 * new failure site in `studio-capability-probes.ts` cannot reach this screen
 * without every locale naming it — that is the whole point of having replaced
 * the probe's pre-rendered English `message` with a code (see
 * `StudioProbeFailure`). `{status}` follows the dictionary's existing
 * placeholder convention (`publishing.exportsCount`'s `{count}`).
 */
function probeFailureDetail(
  failure: StudioProbeFailure,
  copy: RuntimeCopy,
): string {
  const template = copy.probeFailures[failure.code]
  return 'httpStatus' in failure
    ? template.replace('{status}', String(failure.httpStatus))
    : template
}

function capabilityDetail(
  observation: StudioCapabilityObservation,
  copy: RuntimeCopy,
): string | null {
  switch (observation.state) {
    case 'available':
      return null
    case 'unavailable':
      // Authored by the PLATFORM, not by Studio — see the `message` field's
      // own comment on `StudioCapabilityObservation`. Rendered verbatim
      // because Studio cannot localize a sentence it did not write.
      return observation.message
    case 'error':
      return probeFailureDetail(observation.failure, copy)
    case 'unknown':
      return null
  }
}

function capabilityState(
  observation: StudioCapabilityObservation,
  copy: RuntimeCopy,
): string {
  switch (observation.state) {
    case 'available':
      return copy.available
    case 'unavailable':
      return copy.unavailable
    case 'error':
      return copy.readFailed
    case 'unknown':
      return copy.notConfirmed
  }
}

export function RuntimeCapabilityPanel({
  profile,
}: {
  profile: StudioPresentationProfile
}) {
  const { strings } = useStudioLocale()
  const copy = strings.settings.runtime
  const capabilities = Object.entries(profile.capabilities) as Array<[
    StudioCapabilityKey,
    StudioCapabilityObservation,
  ]>

  return (
    <section
      aria-labelledby="runtime-capability-heading"
      className="rounded-xl border border-border bg-card p-6 space-y-4"
      data-testid="runtime-capability-panel"
    >
      <div>
        <h2
          id="runtime-capability-heading"
          className="text-sm font-semibold text-foreground"
        >
          {copy.title}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {copy.note}
        </p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">{copy.distribution}</dt>
          <dd className="text-sm font-medium">
            {profile.distribution === 'unknown' ? '—' : profile.distribution}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{copy.studioSha}</dt>
          <dd className="text-sm font-medium break-all">
            {displayIdentity(profile.studioSourceSha)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{copy.applianceReleaseId}</dt>
          <dd className="text-sm font-medium break-all">
            {displayIdentity(profile.applianceReleaseId)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{copy.observedAt}</dt>
          <dd className="text-sm font-medium">
            {displayIdentity(profile.observedAt)}
          </dd>
        </div>
      </dl>

      <div className="space-y-2">
        <h3 className="text-xs font-medium text-muted-foreground">
          {copy.observationsTitle}
        </h3>
        {capabilities.length === 0 ? (
          <p className="text-sm" data-testid="runtime-capability-unknown">
            {copy.notConfirmed}
          </p>
        ) : (
          <dl className="space-y-2">
            {capabilities.map(([key, observation]) => {
              const detail = capabilityDetail(observation, copy)
              return (
                <div
                  key={key}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
                  data-testid={`runtime-capability-${key}`}
                >
                  <dt className="text-sm text-foreground">{copy.capabilities[CAPABILITY_COPY_KEYS[key]]}</dt>
                  <dd className="text-sm text-muted-foreground">
                    <span className="font-medium">{capabilityState(observation, copy)}</span>
                    {detail && (
                      <>
                        {' — '}
                        <span>{detail}</span>
                      </>
                    )}
                  </dd>
                </div>
              )
            })}
          </dl>
        )}
      </div>
    </section>
  )
}
